import React, { useMemo, useCallback, useRef } from 'react';
import {
    AddContainer,
    Component4,
    Texts,
    Component2,
    InnerComponent,
    SaveButton,
    RemoveButton
} from './Style';
import { Formik, Form, FieldArray } from 'formik';
import InputSelect from 'src/components/shared/Form/Select';
import DateTimePicker from 'src/components/App/DatePicker';
import SwitchComponent from 'src/components/shared/Form/Switch';
import theme from 'src/config/theme';
import { Box, TextField, InputAdornment, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import { ReactComponent as Clock } from '../../../../../assets/images/clock.svg';
import { ReactComponent as Remove } from '../../../../../assets/images/close-circle.svg';
import useResponsive from 'src/components/hooks/useResponsive';
dayjs.extend(customParseFormat);

const Days = [
    { label: 'Monday', value: 'monday' },
    { label: 'Tuesday', value: 'tuesday' },
    { label: 'Wednesday', value: 'wednesday' },
    { label: 'Thursday', value: 'thursday' },
    { label: 'Friday', value: 'friday' },
    { label: 'Saturday', value: 'saturday' },
    { label: 'Sunday', value: 'sunday' },
];

// Static sx objects — created once, not per-render
const selectSx = {
    boxShadow: theme.shadows[1],
    '& .MuiOutlinedInput-root': {
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '12px',
    },
};

// Memoized single timing row — only re-renders when its own data changes
const TimingRow = React.memo(({ timing, index, onDayChange, onStartChange, onEndChange, onRemove }) => {
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '24px';
    const Height = isMobile || isTablet ? '18px' : '24px';

    return (
        <Component2>
            <InnerComponent>
                <Texts>Day</Texts>
                <InputSelect
                    name={`timings[${index}].day`}
                    options={Days}
                    value={timing.day}
                    onChange={onDayChange}
                    placeholder="Select"
                    variant="outlined"
                    sx={selectSx}
                />
            </InnerComponent>

            <InnerComponent>
                <Texts>Start Time</Texts>
                <DateTimePicker
                    type="time"
                    value={timing.startTime}
                    onChange={onStartChange}
                />
            </InnerComponent>

            <InnerComponent>
                <Texts>End Time</Texts>
                <DateTimePicker
                    type="time"
                    value={timing.endTime}
                    onChange={onEndChange}
                />
            </InnerComponent>

            <RemoveButton onClick={onRemove} sx={{ mt: isMobile ? "5px" : 3 }}>
                <Remove width={Width} height={Height} />
            </RemoveButton>
        </Component2>
    );
});

TimingRow.displayName = 'TimingRow';

const DoctorTiming = ({ data, innerRef, setDoctorProfileData }) => {
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '24px';
    const Height = isMobile || isTablet ? '18px' : '24px';

    const parseTime = useCallback((timeStr) => {
        if (!timeStr) return null;
        const normalized = timeStr.replace(/\./g, '').toUpperCase();
        const formats = ['h A', 'h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha'];
        for (let format of formats) {
            const parsed = dayjs(normalized, format, true);
            if (parsed.isValid()) return parsed.toDate();
        }
        return null;
    }, []);

    // Stabilize initial values with data to avoid manual useEffect sync
    const initialValues = useMemo(() => {
        let processedTimings = [];
        try {
            if (Array.isArray(data?.timings) && data.timings.length > 0) {
                // SAFETY: Remove duplicates and limit to 50 items to prevent browser crash
                // The previous infinite loop bug might have created thousands of duplicate entries in the DB
                const seen = new Set();
                const uniqueData = data.timings.filter(item => {
                    const key = `${item?.day}-${item?.startTime}-${item?.endTime}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                }).slice(0, 50);

                console.log(`>>> [DEBUG] Filtered doctorTimings from ${data.timings.length} to ${uniqueData.length} unique items`);

                processedTimings = uniqueData.map((t) => ({
                    ...t,
                    startTime: parseTime(t.startTime),
                    endTime: parseTime(t.endTime),
                }));
            }
        } catch (error) {
            console.error(">>> [DEBUG] Error in initialValues useMemo:", error);
        }

        return {
            timings: processedTimings,
            acceptingAppointments: data?.acceptingAppointments === 'yes',
        };
    }, [data?.timings?.length, data?.acceptingAppointments, parseTime]);

    const handleSubmit = useCallback((values) => {
        const formattedTimings = values.timings.map((t) => ({
            ...t,
            day: t.day?.value || t.day,
            startTime: dayjs(t.startTime).format('hh:mm A'),
            endTime: dayjs(t.endTime).format('hh:mm A'),
        }));

        setDoctorProfileData((prev) => ({
            ...prev,
            doctorTimings: {
                timings: formattedTimings,
                acceptingAppointments: values.acceptingAppointments ? 'yes' : 'no',
            },
        }));
    }, [setDoctorProfileData]);

    // Use a ref to hold latest setFieldValue to avoid re-renders
    const setFieldValueRef = useRef(null);

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} innerRef={innerRef} enableReinitialize={false}>
            {({ values, setFieldValue }) => {
                // Store latest setFieldValue in ref
                setFieldValueRef.current = setFieldValue;

                return (
                    <Form>
                        <AddContainer sx={{ gap: '20px' }}>
                            <FieldArray name="timings">
                                {({ push, remove }) => (
                                    <>
                                        {values.timings?.map((timing, index) => (
                                            <TimingRowWrapper
                                                key={index}
                                                timing={timing}
                                                index={index}
                                                setFieldValueRef={setFieldValueRef}
                                                remove={remove}
                                            />
                                        ))}

                                        <Box display="flex" justifyContent="flex-end" mt={2}>
                                            <SaveButton
                                                variant="outlined"
                                                onClick={() => {
                                                    push({
                                                        day: '',
                                                        startTime: null,
                                                        endTime: null,
                                                    });
                                                }}
                                                sx={{ mt: 2 }}
                                                startIcon={<Add width={Width} height={Height} sx={{ color: theme.palette.primary.main }} />}
                                            >Add </SaveButton>
                                        </Box>
                                    </>
                                )}
                            </FieldArray>

                            <Component4
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Texts>Accepting Appointments</Texts>
                                <SwitchComponent
                                    name="acceptingAppointments"
                                    checked={values.acceptingAppointments}
                                    onChange={() =>
                                        setFieldValue('acceptingAppointments', !values.acceptingAppointments)
                                    }
                                    sx={{ margin: 0, padding: 0 }}
                                />
                            </Component4>
                        </AddContainer>
                    </Form>
                );
            }}
        </Formik>
    );
};

// Wrapper that creates stable callbacks using ref
const TimingRowWrapper = React.memo(({ timing, index, setFieldValueRef, remove }) => {
    const onDayChange = useCallback((val) => {
        setFieldValueRef.current(`timings[${index}].day`, val);
    }, [index, setFieldValueRef]);

    const onStartChange = useCallback((val) => {
        setFieldValueRef.current(`timings[${index}].startTime`, val);
    }, [index, setFieldValueRef]);

    const onEndChange = useCallback((val) => {
        setFieldValueRef.current(`timings[${index}].endTime`, val);
    }, [index, setFieldValueRef]);

    const onRemove = useCallback(() => {
        remove(index);
    }, [index, remove]);

    return (
        <TimingRow
            timing={timing}
            index={index}
            onDayChange={onDayChange}
            onStartChange={onStartChange}
            onEndChange={onEndChange}
            onRemove={onRemove}
        />
    );
});

TimingRowWrapper.displayName = 'TimingRowWrapper';

export default DoctorTiming;
